import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaMapMarkerAlt, FaCar, FaHistory, FaTools, FaEdit, FaArrowLeft, FaPrint, FaPhone, FaUser } from 'react-icons/fa';
import axios from 'axios';

const StationProfil = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isAdmin = (() => {
    try { return localStorage.getItem('userRole') === 'admin'; } catch (_) { return false; }
  })();
  const [stationData, setStationData] = useState(null);
  const [serviceHistorik, setServiceHistorik] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('info');
  const [showAddBilModal, setShowAddBilModal] = useState(false);
  const [apiLookup, setApiLookup] = useState({ loading: false, error: '', data: null });
  const [formData, setFormData] = useState({
    internt_nummer: '',
    nummerplade: '',
    model: '',
    vogn_type: '',
    status: 'I drift',
    type: 'Almindelig',
    station_id: ''
  });

  const vognTypeOptions = useMemo(() => {
    const unique = new Set();
    const standard = stationData?.biler?.standard || [];
    const laanebiler = stationData?.biler?.laanebiler || [];
    [...standard, ...laanebiler].forEach((bil) => {
      const type = (bil?.vogn_type || '').trim();
      if (type) unique.add(type);
    });
    return Array.from(unique).sort((a, b) => a.localeCompare(b, 'da', { sensitivity: 'base' }));
  }, [stationData]);

  useEffect(() => {
    fetchStationData();
    fetchServiceHistorik();
  }, [id]);

  const fetchStationData = async () => {
    try {
      console.log('Fetching station data for ID:', id);
      const response = await axios.get(`/api/stationer/profil/${id}`);
      console.log('Station data response:', response.data);
      setStationData(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching station data:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      setLoading(false);
    }
  };

  const fetchServiceHistorik = async () => {
    try {
      // Hent service historik for alle biler på denne station
      const response = await axios.get(`/api/services/station/${id}/historik`);
      setServiceHistorik(response.data);
    } catch (error) {
      console.error('Error fetching service history:', error);
      setServiceHistorik([]);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('da-DK');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'I drift': return 'bg-green-100 text-green-800 dark:bg-green-900/70 dark:text-green-200';
      case 'På værksted': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/70 dark:text-yellow-200';
      case 'Udlånt': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/70 dark:text-blue-200';
      case 'Reserve': return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
      case 'Til salg': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/70 dark:text-purple-200';
      case 'På auktion': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/70 dark:text-purple-200';
      case 'Solgt': return 'bg-red-100 text-red-800 dark:bg-red-900/70 dark:text-red-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const printProfil = () => {
    window.print();
  };

  const openMapsForAddress = (address) => {
    if (!address) return;
    const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-falck-blue"></div>
      </div>
    );
  }

  if (!stationData) {
    return (
      <div className="p-6">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-600">Station ikke fundet</h2>
          <button
            onClick={() => navigate('/stationer')}
            className="mt-4 px-4 py-2 bg-falck-blue text-white rounded-lg hover:bg-blue-700"
          >
            Tilbage til stationer
          </button>
        </div>
      </div>
    );
  }

  const { station, biler, laanehistorik, statistik } = stationData;
  const stationLabel = station?.stationsnummer
    ? `${station.stationsnummer} - ${station.navn}`
    : station?.navn;

  const resetAddBilForm = (stationId) => {
    setFormData({
      internt_nummer: '',
      nummerplade: '',
      model: '',
      vogn_type: '',
      status: 'I drift',
      type: 'Almindelig',
      station_id: stationId || ''
    });
    setApiLookup({ loading: false, error: '', data: null });
  };

  const handleAddBilForStation = () => {
    if (!station?.id) return;
    resetAddBilForm(station.id);
    setShowAddBilModal(true);
  };

  const handleFetchDMR = async () => {
    if (!formData.nummerplade.trim()) {
      setApiLookup({ loading: false, error: 'Indtast en nummerplade', data: null });
      return;
    }
    try {
      setApiLookup({ loading: true, error: '', data: null });
      const res = await axios.post('/api/biler/dmr-lookup', { nummerplade: formData.nummerplade.trim() });
      const data = res.data?.data || null;
      if (data) {
        setApiLookup({ loading: false, error: '', data });
        setFormData((prev) => ({
          ...prev,
          model: prev.model || data.model || prev.model,
          vogn_type: prev.vogn_type || data.art || prev.vogn_type
        }));
      } else {
        setApiLookup({ loading: false, error: 'Ingen data retur fra DMR', data: null });
      }
    } catch (e) {
      const msg = e.response?.data?.message || 'Fejl ved opslag';
      setApiLookup({ loading: false, error: msg, data: null });
    }
  };

  const handleAddBilSubmit = async (e) => {
    e.preventDefault();
    if (!station?.id) return;
    try {
      const payload = { ...formData, station_id: station.id };
      if (apiLookup.data) {
        const d = apiLookup.data;
        payload['stelnummer'] = d['stelnummer'] ?? null;
        payload['mærke'] = d['mærke'] ?? null;
        payload['art'] = d['art'] ?? null;
        payload['første_registreringsdato'] = d['første_registreringsdato'] ?? null;
        payload['anvendelse'] = d['anvendelse'] ?? null;
        payload['mot_type'] = d['mot_type'] ?? null;
        payload['mot_dato'] = d['mot_dato'] ?? null;
        payload['mot_resultat'] = d['mot_resultat'] ?? null;
        payload['mot_status'] = d['mot_status'] ?? null;
        payload['mot_kilometerstand'] = d['mot_kilometerstand'] ?? null;
        if (!payload.model && d['model']) payload.model = d['model'];
      }
      await axios.post('/api/biler', payload);
      setShowAddBilModal(false);
      resetAddBilForm(station.id);
      fetchStationData();
    } catch (error) {
      console.error('Error saving bil:', error);
    }
  };

  return (
    <div className="px-2 sm:px-4 md:px-6 py-2 sm:py-4">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/stationer')}
            className="p-2 text-gray-600 hover:text-gray-800"
          >
            <FaArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">{station.navn}</h1>
            <p className="text-gray-600">
              {station.stationsnummer ? `Stationsnummer: ${station.stationsnummer} • ` : ''}{station.adresse}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={printProfil}
            className="bg-gray-600 text-white px-3 py-2 rounded-lg flex items-center gap-2 hover:bg-gray-700"
          >
            <FaPrint /> <span className="hidden sm:inline">Print</span>
          </button>
          {localStorage.getItem('userRole') === 'admin' && (
            <button
              onClick={() => navigate(`/stationer/edit/${station.id}`)}
              className="bg-falck-blue text-white px-3 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700"
              title="Rediger"
            >
              <FaEdit /> <span className="hidden sm:inline">Rediger</span>
            </button>
          )}
        </div>
      </div>

      {/* Statistik Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <FaCar className="text-falck-blue text-2xl mr-3" />
            <div>
              <p className="text-sm text-gray-600">Total biler</p>
              <p className="text-2xl font-bold text-gray-900">{statistik.total_biler || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <FaCar className="text-green-600 text-2xl mr-3" />
            <div>
              <p className="text-sm text-gray-600">I drift</p>
              <p className="text-2xl font-bold text-gray-900">{statistik.i_drift || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <FaCar className="text-blue-600 text-2xl mr-3" />
            <div>
              <p className="text-sm text-gray-600">Lånebiler</p>
              <p className="text-2xl font-bold text-gray-900">{statistik.laanebiler || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <FaCar className="text-yellow-600 text-2xl mr-3" />
            <div>
              <p className="text-sm text-gray-600">På værksted</p>
              <p className="text-2xl font-bold text-gray-900">{statistik.paa_vaerksted || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <div className="overflow-x-auto no-scrollbar">
          <nav className="-mb-px flex space-x-6 sm:space-x-8 whitespace-nowrap px-1">
            <button
              onClick={() => setActiveTab('info')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'info'
                  ? 'border-falck-blue text-falck-blue'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FaMapMarkerAlt className="inline mr-2" />
              Information
            </button>
            <button
              onClick={() => setActiveTab('biler')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'biler'
                  ? 'border-falck-blue text-falck-blue'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FaCar className="inline mr-2" />
              Biler ({statistik.total_biler || 0})
            </button>
            <button
              onClick={() => setActiveTab('laanehistorik')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'laanehistorik'
                  ? 'border-falck-blue text-falck-blue'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FaHistory className="inline mr-2" />
              Lånehistorik
            </button>
            <button
              onClick={() => setActiveTab('service')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'service'
                  ? 'border-falck-blue text-falck-blue'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FaTools className="inline mr-2" />
              Service Historik
            </button>
          </nav>
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow">
        {activeTab === 'info' && (
          <div className="px-2 sm:px-4 md:px-6 py-2 sm:py-4">
            <h2 className="text-xl font-semibold mb-4">Station Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-medium text-gray-900 mb-3">Grundlæggende Information</h3>
                <dl className="space-y-3">
                  <div className="flex items-center gap-3">
                    <FaMapMarkerAlt className="text-gray-400" />
                    <div>
                      <dt className="text-sm text-gray-600">Navn</dt>
                      <dd className="font-medium">{station.navn}</dd>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <FaMapMarkerAlt className="text-gray-400" />
                    <div>
                      <dt className="text-sm text-gray-600">Adresse</dt>
                      <dd className="font-medium">
                        <button
                          type="button"
                          onClick={() => openMapsForAddress(station.adresse)}
                          onDoubleClick={() => { try { navigator.clipboard.writeText(station.adresse || ''); } catch (_) {} }}
                          className="text-falck-blue hover:underline"
                          title="Åbn i kort eller dobbeltklik for at kopiere"
                        >
                          {station.adresse}
                        </button>
                      </dd>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <FaUser className="text-gray-400" />
                    <div>
                      <dt className="text-sm text-gray-600">Stationsleder</dt>
                      <dd className="font-medium">{station.stationsleder || 'Ikke angivet'}</dd>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <FaPhone className="text-gray-400" />
                    <div>
                      <dt className="text-sm text-gray-600">Telefonnummer</dt>
                      <dd className="font-medium">
                        {station.telefonnummer ? (
                          <a href={`tel:${station.telefonnummer}`} className="text-falck-blue hover:underline" title="Ring">
                            {station.telefonnummer}
                          </a>
                        ) : 'Ikke angivet'}
                      </dd>
                    </div>
                  </div>
                  {station.doerkode && (
                    <div className="flex items-center gap-3">
                      <FaMapMarkerAlt className="text-gray-400" />
                      <div>
                        <dt className="text-sm text-gray-600">Dørkode</dt>
                        <dd className="font-medium">{station.doerkode}</dd>
                      </div>
                    </div>
                  )}
                </dl>
              </div>
              
              <div>
                <h3 className="font-medium text-gray-900 mb-3">Statistik</h3>
                <dl className="space-y-2">
                  <div className="flex justify-between">
                    <dt className="text-gray-600">Total biler:</dt>
                    <dd className="font-medium">{statistik.total_biler || 0}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-600">I drift:</dt>
                    <dd className="font-medium">{statistik.i_drift || 0}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-600">På værksted:</dt>
                    <dd className="font-medium">{statistik.paa_vaerksted || 0}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-600">Udlånt:</dt>
                    <dd className="font-medium">{statistik.udlaant || 0}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-600">Reserve:</dt>
                    <dd className="font-medium">{statistik.reserve || 0}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-600">Lånebiler:</dt>
                    <dd className="font-medium">{statistik.laanebiler || 0}</dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'biler' && (
          <div className="px-2 sm:px-4 md:px-6 py-2 sm:py-4">
            <div className="flex items-center justify-between gap-3 mb-4">
              <h2 className="text-xl font-semibold">Biler på stationen</h2>
              {isAdmin && (
                <button
                  type="button"
                  onClick={handleAddBilForStation}
                  className="bg-falck-blue text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700"
                >
                  Tilføj bil
                </button>
              )}
            </div>
            
                    {/* Almindelige biler */}
        {biler.standard.length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Almindelige biler ({biler.standard.length})</h3>
                <div className="scroll-fade-x"><div className="scroll-area">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Internt nr.
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Vogn type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Nummerplade
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Model
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800">
                      {biler.standard.map((bil) => (
                        <tr key={bil.id} className="hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer" onClick={() => navigate(`/biler/profil/${bil.id}`)}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                            {bil.internt_nummer}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                            {bil.vogn_type || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                            {bil.nummerplade || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                            {bil.model || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(bil.status)}`}>
                              {bil.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div></div>
              </div>
            )}

            {/* Lånebiler */}
            {biler.laanebiler.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Lånebiler ({biler.laanebiler.length})</h3>
            <div className="scroll-fade-x"><div className="scroll-area">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Internt nr.
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Vogn type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Nummerplade
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                      </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800">
                  {biler.laanebiler.map((bil) => (
                    <tr key={bil.id} className="hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer" onClick={() => navigate(`/biler/profil/${bil.id}`)}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                        {bil.internt_nummer}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                        {bil.vogn_type || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                        {bil.nummerplade || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(bil.status)}`}>
                          {bil.status}
                        </span>
                      </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div></div>
              </div>
            )}

            {biler.standard.length === 0 && biler.laanebiler.length === 0 && (
              <p className="text-gray-500">Ingen biler tilknyttet denne station</p>
            )}
          </div>
        )}

        {activeTab === 'laanehistorik' && (
          <div className="px-2 sm:px-4 md:px-6 py-2 sm:py-4">
            <h2 className="text-xl font-semibold mb-4">Lånehistorik</h2>
            {laanehistorik.length > 0 ? (
              <div className="scroll-fade-x"><div className="scroll-area">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Periode
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Lånebil
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Erstatter
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800">
                    {laanehistorik.map((historik, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {formatDate(historik.startdato)}
                          {historik.slutdato && (
                            <span> - {formatDate(historik.slutdato)}</span>
                          )}
                          {!historik.slutdato && (
                            <span className="text-blue-600 dark:text-blue-300 font-medium"> (Aktiv)</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {historik.bil_internt_nummer} - {historik.bil_nummerplade}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {historik.erstattet_bil_internt_nummer || 'Ingen'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div></div>
            ) : (
              <p className="text-gray-500">Ingen lånehistorik fundet</p>
            )}
          </div>
        )}

        {activeTab === 'service' && (
          <div className="p-6">
            <h2 className="text-xl font-semibold mb-4">Service Historik</h2>
            {serviceHistorik.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Dato
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Bil
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Kategori
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800">
                    {serviceHistorik.map((service, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {formatDate(service.completed_date)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {service.internt_nummer} - {service.nummerplade || 'Ingen nummerplade'}
                          {service.model && <span className="text-gray-500 dark:text-gray-300"> ({service.model})</span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {service.category_name || 'Ukendt kategori'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900/70 dark:text-green-200">
                            Gennemført
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500">Ingen service historik fundet</p>
            )}
          </div>
        )}
      </div>

      {showAddBilModal && (
        <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-gray-600/60 px-3 sm:px-4 py-6 overflow-y-auto">
          <div className="w-full max-w-3xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow-xl p-6 sm:p-8 max-h-[90vh] overflow-y-auto">
            <div className="mt-1">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                Tilføj ny bil
              </h3>
              <form onSubmit={handleAddBilSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Internt nummer *</label>
                  <input
                    type="text"
                    required
                    value={formData.internt_nummer}
                    onChange={(e) => setFormData({ ...formData, internt_nummer: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-falck-blue focus:border-falck-blue"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Nummerplade</label>
                  <input
                    type="text"
                    value={formData.nummerplade}
                    onChange={(e) => setFormData({ ...formData, nummerplade: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-falck-blue focus:border-falck-blue"
                  />
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={handleFetchDMR}
                      className="px-3 py-1.5 text-sm bg-gray-800 text-white rounded hover:bg-gray-900 disabled:opacity-60"
                      disabled={apiLookup.loading}
                    >
                      {apiLookup.loading ? 'Henter...' : 'Hent data'}
                    </button>
                    {apiLookup.error && (
                      <span className="text-sm text-red-600">{apiLookup.error}</span>
                    )}
                  </div>
                  {apiLookup.data && (
                    <div className="mt-3 text-sm text-gray-700 dark:text-gray-200">
                      <div><strong>Mærke:</strong> {apiLookup.data['mærke'] || '-'}</div>
                      <div><strong>Model:</strong> {apiLookup.data['model'] || '-'}</div>
                      <div><strong>Stelnummer:</strong> {apiLookup.data['stelnummer'] || '-'}</div>
                      <div><strong>Første registreringsdato:</strong> {apiLookup.data['første_registreringsdato'] || '-'}</div>
                      <div><strong>Art:</strong> {apiLookup.data['art'] || '-'}</div>
                      <div><strong>Anvendelse:</strong> {apiLookup.data['anvendelse'] || '-'}</div>
                      <div><strong>Synstype:</strong> {apiLookup.data['mot_type'] || '-'}</div>
                      <div><strong>Synsdato:</strong> {apiLookup.data['mot_dato'] || '-'}</div>
                      <div><strong>Synsresultat:</strong> {apiLookup.data['mot_resultat'] || '-'}</div>
                      <div><strong>Synsstatus:</strong> {apiLookup.data['mot_status'] || '-'}</div>
                      <div><strong>Kilometerstand:</strong> {apiLookup.data['mot_kilometerstand'] || '-'}</div>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Model</label>
                  <input
                    type="text"
                    value={formData.model}
                    onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-falck-blue focus:border-falck-blue"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Vogn type</label>
                  <input
                    type="text"
                    list="station-vogn-type-options"
                    value={formData.vogn_type}
                    onChange={(e) => setFormData({ ...formData, vogn_type: e.target.value })}
                    placeholder={vognTypeOptions.length ? 'Skriv eller vælg en eksisterende type' : ''}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-falck-blue focus:border-falck-blue"
                  />
                  {vognTypeOptions.length > 0 && (
                    <datalist id="station-vogn-type-options">
                      {vognTypeOptions.map((type) => (
                        <option key={type} value={type} />
                      ))}
                    </datalist>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-falck-blue focus:border-falck-blue"
                  >
                    <option value="I drift">I drift</option>
                    <option value="På værksted">På værksted</option>
                    <option value="Udlånt">Udlånt</option>
                    <option value="Reserve">Reserve</option>
                    <option value="Til salg">Til salg</option>
                    <option value="Solgt">Solgt</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Type</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-falck-blue focus:border-falck-blue"
                  >
                    <option value="Almindelig">Almindelig</option>
                    <option value="Lånebil">Lånebil</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Station</label>
                  <input
                    type="text"
                    value={stationLabel || ''}
                    disabled
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-600"
                  />
                </div>
                <div className="flex flex-col sm:flex-row sm:justify-end gap-2 sm:gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddBilModal(false);
                      resetAddBilForm(station?.id);
                    }}
                    className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-200 dark:bg-gray-800 rounded-md hover:bg-gray-300 dark:hover:bg-gray-700"
                  >
                    Annuller
                  </button>
                  <button
                    type="submit"
                    className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-falck-blue rounded-md hover:bg-blue-700"
                  >
                    Opret
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StationProfil;
